import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { formatMoney } from '../utils/currency';
import { Scale, BookOpen, Users, Receipt } from 'lucide-react';

const BUCKETS = [
  ['current', 'Current'], ['1_30', '1-30 days'], ['31_60', '31-60 days'], ['61_90', '61-90 days'], ['over_90', '90+ days'],
];

const typeBadge = (t) => ({
  asset: 'bg-blue-100 text-blue-800', liability: 'bg-red-100 text-red-800', equity: 'bg-purple-100 text-purple-800',
  revenue: 'bg-green-100 text-green-800', expense: 'bg-orange-100 text-orange-800',
}[t] || 'bg-gray-100 text-gray-700');

export const TrialBalanceReport = ({ data }) => {
  if (!data) return null;
  const cur = data.currency;
  return (
    <Card className="shadow-md" data-testid="trial-balance-report">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2"><Scale className="h-5 w-5 text-blue-600" /> Trial Balance</CardTitle>
          <span className={`text-xs font-semibold px-2 py-1 rounded ${data.balanced ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-testid="trial-balance-status">
            {data.balanced ? 'Debits = Credits ✓' : 'Out of balance'} · Accrual basis · {cur}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Account</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-right px-3 py-2">Debit</th>
                <th className="text-right px-3 py-2">Credit</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.accounts.map((a) => (
                <tr key={a.account} className="hover:bg-gray-50" data-testid={`tb-row-${a.account.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                  <td className="px-3 py-2 font-medium">{a.account}</td>
                  <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold capitalize ${typeBadge(a.type)}`}>{a.type}</span></td>
                  <td className="px-3 py-2 text-right font-mono">{a.balance_debit ? formatMoney(a.balance_debit, cur) : ''}</td>
                  <td className="px-3 py-2 text-right font-mono">{a.balance_credit ? formatMoney(a.balance_credit, cur) : ''}</td>
                </tr>
              ))}
              {data.accounts.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400">No transactions in this period.</td></tr>}
            </tbody>
            <tfoot>
              <tr className="bg-blue-50 font-bold border-t-2 border-blue-300" data-testid="tb-totals">
                <td className="px-3 py-2" colSpan={2}>TOTAL</td>
                <td className="px-3 py-2 text-right font-mono">{formatMoney(data.total_debit, cur)}</td>
                <td className="px-3 py-2 text-right font-mono">{formatMoney(data.total_credit, cur)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export const GeneralLedgerReport = ({ data, accountFilter, onAccountChange }) => {
  if (!data) return null;
  const cur = data.currency;
  return (
    <div className="space-y-4" data-testid="general-ledger-report">
      <Card className="shadow-md">
        <CardContent className="pt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 font-semibold text-gray-800"><BookOpen className="h-5 w-5 text-blue-600" /> General Ledger <span className="text-xs text-gray-500 font-normal">· {cur}</span></div>
          <div className="sm:ml-auto w-full sm:w-72">
            <Select value={accountFilter || 'all'} onValueChange={(v) => onAccountChange(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="gl-account-filter"><SelectValue placeholder="All accounts" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All accounts</SelectItem>
                {(data.account_names || []).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      {data.accounts.length === 0 && <Card><CardContent className="py-8 text-center text-gray-400">No ledger entries in this period.</CardContent></Card>}
      {data.accounts.map((a) => (
        <Card key={a.account} className="shadow-sm" data-testid={`gl-account-${a.account.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">{a.account} <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold capitalize ${typeBadge(a.type)}`}>{a.type}</span></CardTitle>
              <span className="text-sm font-mono font-semibold">Closing: {formatMoney(a.closing_balance, cur)}</span>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-2 py-1.5">Date</th>
                    <th className="text-left px-2 py-1.5">Ref</th>
                    <th className="text-left px-2 py-1.5">Description</th>
                    {data.is_holding_company && <th className="text-left px-2 py-1.5">Division</th>}
                    <th className="text-right px-2 py-1.5">Debit</th>
                    <th className="text-right px-2 py-1.5">Credit</th>
                    <th className="text-right px-2 py-1.5">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {a.entries.map((e, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5 whitespace-nowrap">{e.date}</td>
                      <td className="px-2 py-1.5 font-mono text-gray-600">{e.ref}</td>
                      <td className="px-2 py-1.5">{e.description}</td>
                      {data.is_holding_company && <td className="px-2 py-1.5 text-gray-600">{e.division}</td>}
                      <td className="px-2 py-1.5 text-right font-mono">{e.debit ? formatMoney(e.debit, cur) : ''}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{e.credit ? formatMoney(e.credit, cur) : ''}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-medium">{formatMoney(e.balance, cur)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-2 py-1.5" colSpan={data.is_holding_company ? 4 : 3}>Account totals</td>
                    <td className="px-2 py-1.5 text-right font-mono">{formatMoney(a.total_debit, cur)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{formatMoney(a.total_credit, cur)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{formatMoney(a.closing_balance, cur)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export const AgingReport = ({ data, kind }) => {
  if (!data) return null;
  const cur = data.currency;
  const isAR = kind === 'ar';
  const keyLabel = isAR ? 'Customer' : 'Vendor';
  return (
    <div className="space-y-4" data-testid={`${kind}-aging-report`}>
      <Card className="shadow-md">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              {isAR ? <Users className="h-5 w-5 text-blue-600" /> : <Receipt className="h-5 w-5 text-red-600" />}
              {isAR ? 'Accounts Receivable Aging' : 'Accounts Payable Aging'}
            </CardTitle>
            <span className="text-xs text-gray-500">As of {data.as_of} · {cur}</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
            {BUCKETS.map(([k, label]) => (
              <div key={k} className="rounded-lg bg-gray-50 p-3 text-center" data-testid={`${kind}-bucket-${k}`}>
                <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
                <p className="font-semibold font-mono text-sm">{formatMoney(data.totals[k], cur)}</p>
              </div>
            ))}
            <div className={`rounded-lg p-3 text-center ${isAR ? 'bg-blue-600' : 'bg-red-600'} text-white`} data-testid={`${kind}-bucket-total`}>
              <p className="text-[11px] uppercase tracking-wide opacity-80">Total {isAR ? 'Owed to you' : 'You owe'}</p>
              <p className="font-bold font-mono text-sm">{formatMoney(data.totals.total, cur)}</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">{keyLabel}</th>
                  {BUCKETS.map(([k, label]) => <th key={k} className="text-right px-3 py-2">{label}</th>)}
                  <th className="text-right px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.rows.map((r) => (
                  <tr key={r.name} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{r.name} <span className="text-xs text-gray-400">({r.count})</span></td>
                    {BUCKETS.map(([k]) => <td key={k} className={`px-3 py-2 text-right font-mono ${k === 'over_90' && r[k] ? 'text-red-600 font-semibold' : ''}`}>{r[k] ? formatMoney(r[k], cur) : ''}</td>)}
                    <td className="px-3 py-2 text-right font-mono font-semibold">{formatMoney(r.total, cur)}</td>
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">{isAR ? 'No outstanding invoices.' : 'No unpaid bills.'}</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      {data.details.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="py-3"><CardTitle className="text-base">{isAR ? 'Open Invoices' : 'Unpaid Bills'}</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-2 py-1.5">Ref</th>
                    <th className="text-left px-2 py-1.5">{keyLabel}</th>
                    {!isAR && <th className="text-left px-2 py-1.5">Description</th>}
                    {data.is_holding_company && <th className="text-left px-2 py-1.5">Division</th>}
                    <th className="text-left px-2 py-1.5">Due</th>
                    <th className="text-right px-2 py-1.5">Days Past Due</th>
                    <th className="text-right px-2 py-1.5">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.details.map((d) => (
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5 font-mono">{d.ref}</td>
                      <td className="px-2 py-1.5">{isAR ? d.client_name : d.vendor}</td>
                      {!isAR && <td className="px-2 py-1.5 text-gray-600">{d.description}</td>}
                      {data.is_holding_company && <td className="px-2 py-1.5 text-gray-600">{d.division}</td>}
                      <td className="px-2 py-1.5 whitespace-nowrap">{d.due}</td>
                      <td className={`px-2 py-1.5 text-right font-mono ${d.days_past_due > 90 ? 'text-red-600 font-semibold' : d.days_past_due > 0 ? 'text-amber-700' : ''}`}>{d.days_past_due}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-medium">{formatMoney(d.balance, cur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
